import React, {useEffect, useRef, useState} from 'react'
import { userEdit } from "../../../../actions/user";
import { useDispatch } from "react-redux";
import axios from 'axios'
import { successNotify, errorNotify } from '../../../../utils/app'
import "./Profile.scss"
import SwitchItem from "../../../UI/SwitchItem";
import ReactTooltip from "react-tooltip";

const AccountSettingsProfile = ({ profile }) => {
    const dispatch = useDispatch()
    const { firstname, lastname, email, otp_enabled, owner_otp_enabled, role} = profile.attributes
    const userForm = useRef()

    const [step, setStep] = useState('idle')
    const [qrSvg, setQrSvg] = useState('')
    const [code, setCode] = useState('')
    const [otpError, setOtpError] = useState('')
    const [backupCodes, setBackupCodes] = useState([])
    const [otpEnabled, setOtpEnabled] = useState(otp_enabled || false)
    const [loading, setLoading] = useState(false)

    const handleUserEdit = (e) => {
        e.preventDefault();
        const formData = new FormData(userForm.current);
        dispatch(userEdit(formData))
    }

    const startSetup = async () => {
        setOtpError('')
        setLoading(true)
        try {
            const { data } = await axios.get('/users/two_factor')
            setQrSvg(data.qr_svg)
            setStep('setup')
        } catch (e) {
            setOtpError('Something went wrong, please try again')
        } finally {
            setLoading(false)
        }
    }

    const handleEnable = async (e) => {
        e.preventDefault()
        setOtpError('')
        setLoading(true)
        try {
            const { data } = await axios.post('/users/two_factor/enable', { code })
            setBackupCodes(data.backup_codes)
            setOtpEnabled(true)
            setStep('backup_codes')
            setCode('')
        } catch (e) {
            setOtpError(e.response?.data?.error || 'Invalid code, please try again')
        } finally {
            setLoading(false)
        }
    }

    const handleDisable = async (e) => {
        e.preventDefault()
        setOtpError('')
        setLoading(true)
        try {
            await axios.post('/users/two_factor/disable', { code })
            setOtpEnabled(false)
            setStep('idle')
            setCode('')
            successNotify('Two-factor authentication disabled')
        } catch (e) {
            setOtpError(e.response?.data?.error || 'Invalid code, please try again')
        } finally {
            setLoading(false)
        }
    }

    const codeInput = (onSubmit) => (
        <div className="form-group col-md-3 mb-2">
            <input
                type="text"
                className={`form-control ${otpError ? 'is-invalid' : ''}`}
                placeholder="Enter 6-digit code"
                maxLength={10}
                autoFocus
                value={code}
                onChange={e => { setCode(e.target.value); setOtpError('') }}
                onKeyDown={e => e.key === 'Enter' && onSubmit(e)}
            />
            {otpError && <div className="invalid-feedback d-block">{otpError}</div>}
        </div>
    )

    useEffect(() => {
        setOtpEnabled(otp_enabled || false)
    }, [otp_enabled])

    return (
        <>
            <div className="my-4">
                Manage your user information and login credentials.
            </div>

            <form onSubmit={handleUserEdit} ref={userForm}>
                <div className="row profile-row">
                    <div className="col-lg-4">
                        <div className="form-group mb-2">
                            <label>First name</label>
                            <input type="text" name="user[firstname]" defaultValue={firstname} className="form-control" />
                        </div>
                        <div className="form-group mb-2">
                            <label>Last name</label>
                            <input type="text" name="user[lastname]" defaultValue={lastname} className="form-control" />
                        </div>
                        <div className="form-group mb-2">
                            <label>
                                Email
                                <small className="mx-1">(login username)</small>
                            </label>
                            <input type="email" name="user[email]" defaultValue={email} className="form-control" required />
                        </div>
                    </div>
                </div>
                <div className="row profile-row mb-4">
                    <div className="col-lg-4">
                        <div className="form-group">
                            <label>Create new password</label>
                            <input type="password" name="user[password]" autoComplete="new-password" className="form-control" />
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="form-group">
                            <label>Repeat new password</label>
                            <input type="password" name="user[password_confirmation]" autoComplete="false" className="form-control" />
                        </div>
                    </div>
                </div>
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <button type="submit" className="btn btn-primary float-end">Save</button>
                        <button type="reset" className="btn btn-secondary float-end mx-2">Cancel</button>
                    </div>
                </div>
            </form>

            <div className="my-4">
                <h6>Two-factor authentication</h6>
            </div>

            <SwitchItem
                name="otp_enabled"
                wrapperClass="col-lg-8 settings-item"
                title="Two-factor authentication"
                disabled={owner_otp_enabled}
                info={owner_otp_enabled ? "Two-factor authentication is required by your account owner" : null}
                desc={<small className="fs-08">Require an authentication code in addition to your password when signing in.</small>}
                checked={otpEnabled}
                changeHandler={() => {
                    setOtpError('')
                    setCode('')
                    otpEnabled ? setStep(step === 'disable' ? 'idle' : 'disable') : startSetup()
                }}
            >
                {step === 'backup_codes' && (
                    <div className="ml-4 mt-3">
                        <p className="fs-08 mb-2">Save your backup codes. Each can only be used once.</p>
                        {backupCodes.map((c, i) => (
                            <code key={i} className="d-block p-2 bg-light mb-1">{c}</code>
                        ))}
                        <p className="fs-08 mt-2">⚠️ These codes will not be shown again</p>
                        <button className="btn btn-primary btn-sm mt-2" onClick={() => setStep('idle')}>
                            I've saved my codes
                        </button>
                    </div>
                )}

                {step === 'setup' && (
                    <div className="ml-4 mt-3">
                        <p className="fs-08 mb-2">Scan this QR code with your authenticator app</p>
                        <div className="col-md-3 mb-3" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                        {role === "owner" && <div className="mb-3">
                            <small className="fs-08">The use of two-factor authentication will also be enforced for all sub-users in your account.</small>
                        </div>}
                        {codeInput(handleEnable)}
                        <button className="btn btn-primary btn-sm" onClick={handleEnable} disabled={loading}>
                            {loading ? 'Verifying...' : 'Enable 2FA'}
                        </button>
                        <button className="btn btn-secondary btn-sm mx-2" onClick={() => { setStep('idle'); setCode(''); setOtpError('') }}>
                            Cancel
                        </button>
                    </div>
                )}

                {step === 'disable' && (
                    <div className="ml-4 mt-3">
                        <p className="fs-08 mb-2">Enter the code from your authenticator app to disable 2FA</p>
                        {codeInput(handleDisable)}
                        <button className="btn btn-danger btn-sm" onClick={handleDisable} disabled={loading}>
                            {loading ? 'Verifying...' : 'Disable 2FA'}
                        </button>
                        <button className="btn btn-secondary btn-sm mx-2" onClick={() => { setStep('idle'); setCode(''); setOtpError('') }}>
                            Cancel
                        </button>
                    </div>
                )}
            </SwitchItem>
            <ReactTooltip effect="solid" html={false} className="switch-tooltip"/>
        </>
    )
}

export default React.memo(AccountSettingsProfile)